import { useMemo, useState } from "react";
import { HelperInfoBox, InputFormField, PopupWrapper } from "ui";
import useHprHfrOnboardingStore from "../stores/hpr-hfr-dhis-onboarding";
import {
  HFROnboardingSubStates,
  OnboardingNavigationStates,
} from "../stores/hpr-hfr-dhis-onboarding/enums";
import { ChevronDownRegularIcon } from "@elixir/icons";
import useGetMyProfile from "../apis/axon/get-my-profile";
import useGetHfrDetailsByHfrId from "../apis/axon/hfr/use-get-hfr-details-by-hfr-id";
import usePostExistingHfrToClinic from "../apis/axon/hfr/use-post-hfr-for-clinic";
import { notification } from "antd";

function ExistingHfrModal() {
  const subStateScreen = useHprHfrOnboardingStore(
    (state) => state.subActiveScreen
  );
  const setSubStateScreen = useHprHfrOnboardingStore(
    (state) => state.setSubActiveScreen
  );
  const existingHfrId = useHprHfrOnboardingStore(
    (state) => state.existingHfrId
  );
  const clinicIdForExistingHfr = useHprHfrOnboardingStore(
    (state) => state.clinicIdForExistingHfr
  );
  const clearExistingHfrDetails = useHprHfrOnboardingStore(
    (state) => state.clearExistingHfrDetails
  );
  const setExistingHfrDetails = useHprHfrOnboardingStore(
    (state) => state.setExistingHfrDetails
  );
  const setFacilityId = useHprHfrOnboardingStore(
    (state) => state.setHfrFacilityId
  );
  const setActiveScreen = useHprHfrOnboardingStore(
    (state) => state.setActiveScreen
  );
  const setIsExistingHfrOnboarding = useHprHfrOnboardingStore(
    (state) => state.setIsExistingHfrOnboarding
  );
  const doctorId = useHprHfrOnboardingStore(
    (state) => state.doctorId
  );

  const { mutateAsync: getHfrDetails, isLoading: isHfrDetailsLoading } =
    useGetHfrDetailsByHfrId();

  const { mutateAsync: postHfrToClinic, isLoading: isPostingHfrToClinic } =
    usePostExistingHfrToClinic();

  const [hfrError, setHfrError] = useState("");

  const onExistingHfrSubmission = async () => {
    const { data, error } = await getHfrDetails({ hfrId: existingHfrId });
    if (!data) {
      setHfrError(
        error?.actualMessage ||
          error?.message ||
          "HFR ID May be invalid, please try again with a diferrent HFR ID"
      );
      return;
    }

    const { facilityName, address, facilityId, stateName } = data;

    setExistingHfrDetails({
      facilityId,
      facilityName,
      address: `${address} ${stateName}`,
    });
    setSubStateScreen(HFROnboardingSubStates.CONFIRM_HFR);
  };

  const onHfrDetailsConfirmation = async () => {
    if (!doctorId) {
      notification.error({
        message: "Doctor ID is missing. Please login again.",
        placement: "bottomRight",
      });
      return;
    }
    
    const { success } = await postHfrToClinic({
      clinicId: clinicIdForExistingHfr!,
      hfrId: existingHfrId,
      doctorId: doctorId,
    });

    if (!success) {
      notification.error({
        message: "Failed to link the existing HFR and Your HPR",
        placement: "bottomRight",
      });
    } else {
      setFacilityId(existingHfrId);
      setActiveScreen(OnboardingNavigationStates.HFR_CREATION_COMPLETED);
      clearExistingHfrDetails();
      setIsExistingHfrOnboarding(true);
    }
  };

  const onSubmit = async () => {
    if (subStateScreen === HFROnboardingSubStates.EXISTING_HFR_FORM) {
      onExistingHfrSubmission();
    } else {
      onHfrDetailsConfirmation();
    }
  };

  const onCancel = async () => {
    console.log(subStateScreen);
    if (subStateScreen === HFROnboardingSubStates.CONFIRM_HFR) {
      setSubStateScreen(HFROnboardingSubStates.EXISTING_HFR_FORM);
    } else {
      clearExistingHfrDetails();
    }
  };
  return (
    <PopupWrapper
      title={
        subStateScreen === HFROnboardingSubStates.EXISTING_HFR_FORM
          ? "Enter your Existing HFR"
          : "Confirm your HFR Details"
      }
      open={!!subStateScreen}
      onClose={onCancel}
      isSubmitting={isHfrDetailsLoading || isPostingHfrToClinic}
      onOk={onSubmit}
      onCancel={onCancel}
      okText={
        subStateScreen === HFROnboardingSubStates.CONFIRM_HFR
          ? "Confirm Details"
          : "Continue"
      }
      width={500}
      isOkButtonDisabled={
        !existingHfrId || existingHfrId.length < 10 || isHfrDetailsLoading
      }
      zIndex={1200}
    >
      {subStateScreen === HFROnboardingSubStates.EXISTING_HFR_FORM ? (
        <ExisitngHfrForm hfrError={hfrError} />
      ) : (
        <ExistingHfrConfirmation />
      )}
    </PopupWrapper>
  );
}

function ExisitngHfrForm({ hfrError }: { hfrError: string }) {
  const { data: doctorProfile } = useGetMyProfile();
  const preselectedClinicId = useHprHfrOnboardingStore(
    (state) => state.selectedClinicId
  );
  const clinicId = useHprHfrOnboardingStore(
    (state) => state.clinicIdForExistingHfr
  );
  const setClinicId = useHprHfrOnboardingStore(
    (state) => state.setClinicIdForExistingHfr
  );

  const existingHfrId = useHprHfrOnboardingStore(
    (state) => state.existingHfrId
  );
  const setExistingHfrId = useHprHfrOnboardingStore(
    (state) => state.setExistingHfrId
  );

  const clinicsList = useMemo(() => {
    if (!doctorProfile) {
      return [];
    }

    const clinics = doctorProfile?.profile?.professional?.clinics || [];

    const clinic = preselectedClinicId
      ? clinics?.find((item) => item._id === preselectedClinicId) || clinics[0]
      : clinics[0];

    if (!clinicId) {
      setClinicId(clinic._id);
    }

    return clinics;
  }, [!!doctorProfile]);

  const clinic = clinicsList.find((item) => item._id === clinicId);

  return (
    <div className="dr-p-20 dr-bg-darwin-neutral-50">
      <div className="dr-px-16 dr-py-28 dr-flex dr-flex-col dr-space-y-4 dr-bg-darwin-touch-neutral-0 dr-rounded-12">
        <InputFormField
          helperText="HFR ID will be linked against this clinic in eka"
          title="Clinic Name"
          isRequired={true}
          suggestions={clinicsList.map((item) => ({
            id: item._id,
            name: item.name,
          }))}
          value={clinic?.name}
          onSuggestionSelect={({ id }) => {
            setClinicId(id);
          }}
          customSuffix={
            <div className="dr-h-full dr-flex dr-flex-column dr-items-center dr-justify-center dr-px-12 dr-rounded-r-8">
              <ChevronDownRegularIcon className="dr-w-14 dr-h-14 dr-text-doctor-darwin-neutral-800" />
            </div>
          }
        />
        <InputFormField
          title="HFR ID"
          isRequired
          value={existingHfrId}
          onChange={(e) => {
            if (e.target.value.length <= 14) setExistingHfrId(e.target.value);
          }}
          placeholder="Ex: IN0510000242"
          error={hfrError}
        />
        <div className="dr-pt-12">
          <HelperInfoBox
            text={
              "Ensure you are the owner of the already created HFR. Proof of ownership may be required later"
            }
          />
        </div>
      </div>
    </div>
  );
}

function ExistingHfrConfirmation() {
  const { data: doctorProfile } = useGetMyProfile();
  const clinicId = useHprHfrOnboardingStore(
    (state) => state.clinicIdForExistingHfr
  );

  const selectedClinic = useMemo(() => {
    if (!doctorProfile) {
      return;
    }

    const clinics = doctorProfile?.profile?.professional?.clinics || [];
    const clinic = clinics?.find((item) => item._id === clinicId);

    return clinic;
  }, [!!doctorProfile]);
  const existingHfrDetails = useHprHfrOnboardingStore(
    (state) => state.existingHfrDetail
  );
  return (
    <div className="dr-p-20 dr-bg-darwin-neutral-50">
      <div className="dr-p-16 dr-flex dr-flex-col dr-space-y-20 dr-bg-darwin-touch-neutral-0 dr-rounded-12">
        <div className="dr-rounded-12 dr-bg-darwin-neutral-50 dr-border-darwin-touch-neutral-200 dr-p-16 dr-flex dr-flex-col dr-space-y-16">
          <ConfirmDetailLineItem
            title="HFR ID"
            subtext={existingHfrDetails?.facilityId || ""}
          />
          <ConfirmDetailLineItem
            title="Facility Name"
            subtext={existingHfrDetails?.facilityName || ""}
          />
          <ConfirmDetailLineItem
            title="Facility Address"
            subtext={existingHfrDetails?.address || ""}
          />
        </div>
        <HelperInfoBox
          text={
            <>
              We will link this HFR ID to your clinic -{" "}
              <span className={"dr-text-darwin-neutral-1000 SubheadlineBold"}>
                {selectedClinic?.name}
              </span>{" "}
              to update it in Eka’s directory. Don’t worry if the name doesn’t
              match exactly
            </>
          }
        />
      </div>
    </div>
  );
}

function ConfirmDetailLineItem({
  title,
  subtext,
}: {
  title: string;
  subtext: string;
}) {
  return (
    <div className="dr-flex dr-flex-col">
      <p className="HeadlineBold dr-text-darwin-neutral-1000">{title}</p>
      <p className="SubheadlineRegular dr-text-darwin-neutral-800">{subtext}</p>
    </div>
  );
}

export default ExistingHfrModal;
